import { OpenInNew } from "@mui/icons-material";
import {
  alpha,
  Box,
  Stack,
  styled,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { AnimatePresence, useAnimate } from "motion/react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { AttributionMetric } from "features/analytics/types/types";
import {
  AnalyticsChart,
  useChartData,
  metricByAttribution,
} from "features/analytics";
import { useFromToContext } from "Providers/FromToProvider";
import { useSelectedProject } from "Hooks";
import { IChart } from "Interfaces";
import {
  ApiAttributionModel,
  ApiChartAttribute,
  ApiChartTemplate,
  ApiChartType,
  ApiDimension,
  ApiMetric,
  ApiUTMEntity,
} from "@incendium/api";
import CampaignAnalysisTableRow from "../components/CampaignAnalysisTableRow";
import withPaidChannelAttributes from "HoC/withPaidChannelAttributes";
import ProviderIcon from "features/attributionAnalytics/components/ProviderIcon";
import Loading from "Components/Loading/Loading";
import { attributionApiMetrics } from "../constants";

import ConversionMetricHead from "./ConversionMetricHead";
import PublisherConversionsHead from "./PublisherConversionsHead";
import { formatEnumVal } from "Helpers/enumToText";
import { useSmartLinksSetup } from "features/rezonate/hooks/useSmartLinksSetup";
import CampaignCellIcon from "./CampaignCellIon";

const StyledTopTableCell = styled(TableCell)(({ theme }) => ({
  cursor: "pointer",
  height: 56,
  "&:hover": {
    background: alpha(theme.palette.secondary.main, 0.8),
    color: "white",
  },
}));

interface ICampaignAnalysisTableProps {
  title?: string;
  firstCols: { key: ApiDimension; text: string }[];
  onClick?: (v: string) => void;
  selecteValues: string[];
  conversionMetric: AttributionMetric;
  setConversionMetric: (m: AttributionMetric) => void;
  attributionModel: ApiAttributionModel;
  publisherConversion: ApiMetric;
  setPublisherConversion: (m: ApiMetric) => void;
  filters: ApiChartAttribute[];
  currentStep: number;
  step: number;
  onTitleClick?: () => void;
  paidChannelAttributes: ApiChartAttribute[];
  onMetricClick: (m: ApiMetric, d: ApiDimension, dv: string) => void;
  mode: "explore" | "analyze";
  adEntities: ApiUTMEntity[];
}

function CampaignAnalysisTable({
  title,
  firstCols,
  onClick,
  selecteValues,
  conversionMetric,
  setConversionMetric,
  attributionModel,
  publisherConversion,
  setPublisherConversion,
  filters,
  step,
  currentStep,
  onTitleClick,
  paidChannelAttributes,
  onMetricClick,
  mode,
  adEntities,
}: ICampaignAnalysisTableProps) {
  const { chartOutput } = useFromToContext();
  const { selectedProject } = useSelectedProject();
  const isSmartLinksSetup = useSmartLinksSetup();

  const active = useMemo(() => step === currentStep, [step, currentStep]);
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const [scope, animate] = useAnimate();

  // Reset isDataLoaded when active changes
  useEffect(() => {
    if (!active) {
      setIsDataLoaded(false);
    }
  }, [active]);
  const dimensions = useMemo(() => firstCols.map((f) => f.key), [firstCols]);
  const attributionMetric = useMemo(() => {
    return (
      metricByAttribution(attributionModel, conversionMetric) ||
      ApiMetric.METRIC_MACRO_CONVERSIONS_COUNT_EBA
    );
  }, [attributionModel, conversionMetric]);

  const cacMetric = useMemo(() => {
    return (
      metricByAttribution(attributionModel, AttributionMetric.CAC) ||
      ApiMetric.METRIC_CAC_EBA
    );
  }, [attributionModel]);

  const chart: IChart = useMemo(
    () => ({
      dimension: dimensions,
      template: ApiChartTemplate.TABLE,
      type: ApiChartType.TABLE,
      // includePublisherReadyOnly: true,
      yAxisKeys: [
        {
          key: "l",
          fields: [
            ApiMetric.METRIC_PUBLISHER_CLICKS,
            ...(isSmartLinksSetup ? [ApiMetric.METRIC_SMART_LINK_CLICKS] : []),
            ApiMetric.METRIC_SESSIONS_COUNT,
            ApiMetric.METRIC_BOUNCE_RATE,
            ApiMetric.METRIC_PUBLISHER_CLICKS_DIFFERENCE,

            ApiMetric.METRIC_PUBLISHER_IMPRESSIONS,
            ApiMetric.METRIC_PUBLISHER_CTR,
            ApiMetric.METRIC_CALCULATED_CTR,
            ApiMetric.METRIC_PUBLISHER_CPC,
            ApiMetric.METRIC_SPEND,

            ApiMetric.METRIC_AVERAGE_TIME_ON_PAGE_DURING_SESSION,
            ApiMetric.METRIC_EFFECTIVE_SESSION_RATE,
            ApiMetric.METRIC_PAGE_MICRO_CONVERSIONS_COUNT,

            ApiMetric.METRIC_PUBLISHER_CONVERSIONS,
            ApiMetric.METRIC_PUBLISHER_ALL_CONVERSIONS,
            ApiMetric.METRIC_PUBLISHER_SALES_COUNT,
            ApiMetric.METRIC_PUBLISHER_REVENUE,

            ...attributionApiMetrics,
          ],
        },
      ],
      attributes: [...paidChannelAttributes, ...filters],
      displayOptions: {
        noFilters: true,
        noTitle: true,
        noAttributionToggle: true,
        rowsPerPage: 0,
        tableGroupings: [
          {
            name: "Traffic Metrics",
            metrics: [
              ApiMetric.METRIC_PUBLISHER_CLICKS,
              ...(isSmartLinksSetup
                ? [ApiMetric.METRIC_SMART_LINK_CLICKS]
                : []),
              ApiMetric.METRIC_SESSIONS_COUNT,
              ApiMetric.METRIC_PUBLISHER_CLICKS_DIFFERENCE,
            ],
          },
          {
            name: "Click Through Metrics",
            metrics: [
              ApiMetric.METRIC_PUBLISHER_IMPRESSIONS,
              ApiMetric.METRIC_PUBLISHER_CTR,
              ApiMetric.METRIC_CALCULATED_CTR,
            ],
          },
          {
            name: "Cost Metrics",
            metrics: [
              ApiMetric.METRIC_PUBLISHER_CPC,
              ApiMetric.METRIC_SPEND,
              cacMetric,
            ],
          },
          {
            name: "Engagement Metrics",
            metrics: [
              ApiMetric.METRIC_AVERAGE_TIME_ON_PAGE_DURING_SESSION,
              ApiMetric.METRIC_BOUNCE_RATE,
              ApiMetric.METRIC_EFFECTIVE_SESSION_RATE,
              ApiMetric.METRIC_PAGE_MICRO_CONVERSIONS_COUNT,
            ],
          },

          // {
          //   name: attributionMetrics[conversionMetric],
          //   metrics: [attributionMetric],
          // },
        ],
        tableColWidths: {
          [attributionMetric]: 180,
          [publisherConversion]: 180,
        },
      },
    }),
    [
      filters,
      dimensions,
      paidChannelAttributes,
      attributionMetric,
      cacMetric,
      isSmartLinksSetup,
      publisherConversion,
    ],
  );

  const { chartData } = useChartData(
    selectedProject,
    chart,
    step < currentStep ? chartOutput : null,
    { ignoreModel: true },
  );

  const combinedData = useMemo(() => {
    return chartData?.data || [];
  }, [chartData?.data]);

  const tableCustomHeaderRender = useMemo(
    () => ({
      [ApiDimension.DIMENSION_SOURCE]: (props) => {
        return <>Platforms</>;
      },
      [publisherConversion]: () => (
        <PublisherConversionsHead
          publisherConversion={publisherConversion}
          setPublisherConversion={setPublisherConversion}
        />
      ),
      [attributionMetric]: () => {
        return (
          <ConversionMetricHead
            attributionModel={attributionModel}
            conversionMetric={conversionMetric}
            setConversionMetric={setConversionMetric}
          />
        );
      },
      [cacMetric]: () => {
        return <>CAC ({formatEnumVal(attributionModel as string)})</>;
      },
      [ApiDimension.DIMENSION_EXTERNAL_AD_ID]: () => {
        return <>{title || "Ads"}</>;
      },
      [ApiDimension.DIMENSION_CONTENT]: () => {
        return <>{title || "Content"}</>;
      },
    }),
    [
      publisherConversion,
      conversionMetric,
      attributionMetric,
      attributionModel,
      cacMetric,
      setPublisherConversion,
      setConversionMetric,
      title,
    ],
  );

  const tableCustomCellRender = useMemo(
    () => ({
      [firstCols[0].key as ApiDimension]: ({ value, ...rest }) => {
        const adEntity = adEntities.find(
          (ad) => Number(ad.id) === Number(value),
        );
        return (
          <Box
            sx={{ display: "flex", height: "100%", cursor: "pointer" }}
            onClick={(e) => {
              e.preventDefault();
              onClick && onClick(value);
            }}
          >
            <Stack
              direction={"row"}
              spacing={1}
              alignItems="center"
              justifyContent={"flex-start"}
              pl={1}
            >
              {(firstCols[0].key as ApiDimension) ===
              ApiDimension.DIMENSION_EXTERNAL_AD_ID ? (
                <Stack
                  sx={{ transform: "translateX(-16px)" }}
                  direction={"row"}
                  spacing={1}
                  alignItems="center"
                  justifyContent={"flex-start"}
                >
                  <CampaignCellIcon
                    imageUrl={adEntity?.imageUrl || ""}
                    size={30}
                    rounded
                  />
                  <Typography>{adEntity?.name || value}</Typography>
                </Stack>
              ) : (
                <>
                  {(firstCols[0].key as ApiDimension) ===
                    ApiDimension.DIMENSION_SOURCE && (
                    <ProviderIcon provider={value} />
                  )}

                  <Typography>{value}</Typography>
                </>
              )}
            </Stack>
          </Box>
        );
      },
    }),
    [firstCols, onClick, adEntities],
  );

  const hiddenCols = useMemo(
    () =>
      [
        ...attributionApiMetrics,
        ApiMetric.METRIC_PUBLISHER_CONVERSIONS,
        ApiMetric.METRIC_PUBLISHER_ALL_CONVERSIONS,
        ApiMetric.METRIC_PUBLISHER_SALES_COUNT,
        ApiMetric.METRIC_PUBLISHER_REVENUE,
      ].filter(
        (a) => ![attributionMetric, publisherConversion, cacMetric].includes(a),
      ),
    [attributionMetric, publisherConversion, cacMetric],
  );

  useEffect(() => {
    if (isDataLoaded && active && scope.current) {
      animate(scope.current, { opacity: 1 }, { delay: 0.5 });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDataLoaded, active]);

  const handleDataChange = useCallback(() => {
    setIsDataLoaded(true);
  }, []);

  const toSalesClick = useMemo(() => {
    return {
      text: "Click To See Last Click Sales",
      fn: (dimension) =>
        onMetricClick(attributionMetric, firstCols[0].key, `${dimension}`),
      icon: <OpenInNew />,
    };
  }, [attributionMetric, firstCols, onMetricClick]);

  const toLeadsClick = useMemo(() => {
    return {
      text: "Click To See Leads",
      fn: (dimension) =>
        onMetricClick(
          ApiMetric.METRIC_LEADS_COUNT,
          firstCols[0].key,
          `${dimension}`,
        ),
      icon: <OpenInNew />,
    };
  }, [firstCols, onMetricClick]);

  const onClickHandlers = useMemo(
    () => ({
      [ApiMetric.METRIC_SESSIONS_COUNT as string]: [
        {
          text: "Click To See Visits",
          fn: (dimension) =>
            onMetricClick(
              ApiMetric.METRIC_SESSIONS_COUNT,
              firstCols[0].key,
              `${dimension}`,
            ),
          icon: <OpenInNew />,
        },
      ],
      [attributionMetric as string]: [toSalesClick, toLeadsClick],
    }),
    [attributionMetric, firstCols, onMetricClick, toSalesClick, toLeadsClick],
  );

  return (
    <>
      <AnimatePresence>
        <Stack
          sx={{
            height: mode === "analyze" ? "calc(100% - 69px)" : "100%",
          }}
        >
          {active ? (
            <>
              {!isDataLoaded && <Loading />}
              <Box
                display={"flex"}
                sx={{ height: "100%", width: "100%", opacity: 0 }}
                ref={scope}
              >
                <AnalyticsChart
                  onDataChange={handleDataChange}
                  ignoreAttrModel
                  tableCustomHeaderRender={tableCustomHeaderRender}
                  tableCustomCellRender={tableCustomCellRender}
                  tableHiddenCols={hiddenCols}
                  tableSx={{
                    top: -1,
                    bottom: 1,
                    right: 0,
                    left: 0,
                  }}
                  chart={{
                    ...chart,
                    name: "2nd",
                  }}
                  onClick={onClickHandlers}
                />
              </Box>
            </>
          ) : step < currentStep ? (
            <Table sx={{ tableLayout: "fixed" }}>
              <TableHead>
                <TableRow>
                  <StyledTopTableCell onClick={onTitleClick}>
                    <Typography
                      variant="body2"
                      fontWeight={"bold"}
                      noWrap
                      color={"inherit"}
                    >
                      {title}
                    </Typography>
                  </StyledTopTableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {combinedData.map((d, i) => (
                  <CampaignAnalysisTableRow
                    current={false}
                    key={i}
                    data={d}
                    firstCols={firstCols}
                    selecteValues={selecteValues}
                    dimension={dimensions[0]}
                    onClick={onClick}
                  />
                ))}
              </TableBody>
            </Table>
          ) : (
            <Table sx={{ tableLayout: "fixed" }}>
              <TableHead>
                <TableRow>
                  <StyledTopTableCell onClick={onTitleClick}>
                    <Typography
                      variant="body2"
                      fontWeight={"bold"}
                      noWrap
                      color={"inherit"}
                    >
                      {title}
                    </Typography>
                  </StyledTopTableCell>
                </TableRow>
              </TableHead>
            </Table>
          )}
        </Stack>
      </AnimatePresence>
    </>
  );
}

export default withPaidChannelAttributes(CampaignAnalysisTable);
